import { computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useDatapoints } from '@/composables/useDatapoints'
import { useAiChatStore } from '../stores/ai-chat.store'
import { useAiPanelStore } from '../stores/ai-panel.store'

/**
 * DEV-7125 — availability gate + drawer orchestration for the AI panel.
 *
 * Design rationale (review-critical): a blanket `watch(activeConversationId)`
 * recorder is unsound — on first open, loadSessions()'s auto-select mutates the
 * id inside the awaited call, and Vue's pre-flush watcher runs BEFORE the await
 * continuation, recording the most-recent session over the screen's saved
 * mapping before the restore code reads it. Recording therefore happens ONLY at
 * explicit user-intent points (switcher select, send completion) with the screen
 * key captured at intent time, and restore passes the mapping as
 * `preferredSessionId` so loadSessions never auto-selects the wrong chat.
 */

/** Lightweight availability — used by the FAB and by DefaultLayout's drawer v-if. */
export function useAiAvailability() {
  const route = useRoute()
  const { enableAiAssistant } = useDatapoints()
  const isAvailable = computed(
    () => enableAiAssistant.value && route.meta.hideAiFab !== true,
  )
  /** Stable per-screen key; undefined on nameless redirect hops. */
  const screenKey = computed(() =>
    typeof route.name === 'string' ? route.name : undefined,
  )
  return { isAvailable, screenKey }
}

/**
 * Orchestration — called ONLY by AiAssistantDrawer (single watcher site).
 * DefaultLayout's v-if guarantees this composable's watchers exist only where
 * the assistant is available (unmounted on hideAiFab routes / datapoint off).
 */
export function useAiPanel() {
  const panel = useAiPanelStore()
  const chat = useAiChatStore()
  const { screenKey } = useAiAvailability()

  /** Restore this screen's chat. Runs on open and on mount-with-persisted-open. */
  async function restoreForScreen(): Promise<void> {
    const key = screenKey.value
    const preferred = key ? panel.screenToSession[key] : undefined
    await chat.ensureSessionsLoaded(preferred) // preferred beats most-recent on first load
    if (chat.sessionsLoaded) {
      applyScreenMapping() // sole selection owner (no-op if already active)
      void chat.loadSessions() // background reconcile (merge-safe)
    }
  }

  function applyScreenMapping(): void {
    // Never yank an active/preparing stream or a pending upload dialog, and
    // never prune mappings unless a sessions load actually succeeded.
    if (!chat.sessionsLoaded || chat.isBusy || chat.replaceDialog.open) return
    const key = screenKey.value
    if (!key) return
    const mapped = panel.screenToSession[key]
    if (mapped && chat.restoreSelection(mapped)) return
    if (mapped) panel.forgetSession(mapped) // stale id — safe: sessionsLoaded is true
    chat.newChat() // requirement: no mapping → new chat
  }

  /** Explicit recording — the switcher select handler calls this. */
  function recordCurrentScreen(sessionId: string): void {
    if (screenKey.value) panel.recordScreenSession(screenKey.value, sessionId)
  }

  /**
   * send() wrapper: captures the screen at send time; records the id send()
   * actually streamed into (its return value — never re-read activeConversationId).
   */
  async function sendAndRecord(text: string, files: File[] = []): Promise<void> {
    const key = screenKey.value // captured BEFORE any await
    const sessionId = await chat.send(text, files)
    if (key && sessionId) panel.recordScreenSession(key, sessionId)
  }

  // Drawer-lifecycle watchers (exist only while the drawer is mounted):
  watch(
    () => panel.isOpen,
    (open) => {
      if (open) void restoreForScreen()
    },
  )
  watch(screenKey, () => {
    if (panel.isOpen) applyScreenMapping()
  })

  return {
    panel,
    chat,
    screenKey,
    restoreForScreen,
    applyScreenMapping,
    recordCurrentScreen,
    sendAndRecord,
  }
}
